import { defineStore } from 'pinia'
import { getMeta, setMeta, NetworkError } from '@addictivegroup/addictive-offline'
import { api } from '@/lib/http.js'
import { offline, setCurrentUser } from '@/lib/offline.js'
import { setLocale } from '@/i18n/index.js'
import { plain } from '@/lib/plain.js'
import { setTimeZone } from '@/lib/format.js'
import { usePrivacy } from './privacy.js'

/**
 * The signed-in person. Online it comes from Core (/me); offline from the copy kept in the
 * replica at the last successful start, so the application opens without a network (D-35)
 * — for the same person only: a different account on this device gets nothing of it.
 */
export const useSession = defineStore('session', {
  state: () => ({
    me: null,
    mode: 'unknown', // unknown | online | offline
    loading: false,
    error: null,
  }),
  getters: {
    signedIn: (s) => s.me !== null,
    permissions: (s) => new Set(s.me?.permissions || []),
    offlineMode: (s) => s.mode === 'offline',
    groupUuids: (s) => (s.me?.groups || []).map((g) => g.uuid),
    locale: (s) => s.me?.settings?.language || s.me?.locale || 'pl',
  },
  actions: {
    has(permission) {
      return this.permissions.has(permission)
    },
    hasAny(list) {
      return list.some((p) => this.permissions.has(p))
    },
    /** May see at least some records of an entity (all, own, or subordinates'). */
    canSee(entity) {
      return ['view', 'own_view', 'subordinates_view'].some((a) => this.permissions.has(`${entity}:${a}`))
    },
    async load({ force = false } = {}) {
      if (this.me && !force) return this.me
      this.loading = true
      try {
        const body = await api('/me', { timeoutMs: 10000 })
        this.apply(body.data, 'online')
        await setMeta(offline.db, 'me', plain(body.data))
        return this.me
      } catch (error) {
        if (error instanceof NetworkError) {
          const cached = await getMeta(offline.db, 'me')
          if (cached) {
            this.apply(cached, 'offline')
            return this.me
          }
        }
        this.error = error
        throw error
      } finally {
        this.loading = false
      }
    },
    apply(me, mode) {
      this.me = me
      this.mode = mode
      setCurrentUser({ uuid: me.uuid, name: me.name, tenant_uuid: me.tenant?.uuid })
      setLocale(me.settings?.language || me.locale || 'pl')
      setTimeZone(me.time_zone)
      // Privacy mode as the account has it, when it comes from Core (another device may have
      // switched it); a copy kept on this device does not undo what was chosen here (D-260).
      if (mode === 'online') usePrivacy().adopt(me.settings?.privacy_mode)
    },
    async updateSettings(changes) {
      const body = await api('/me/settings', { method: 'PUT', body: changes })
      this.me = { ...this.me, settings: body.data }
      await setMeta(offline.db, 'me', plain(this.me))
      if ('language' in changes) setLocale(body.data.language || this.me.locale || 'pl')
      return body.data
    },
    /** Hard sign-out wipes the device (D-36); a normal one keeps the replica for next time. */
    async forget({ wipe = false } = {}) {
      if (wipe) await offline.wipe({ keepDevice: false })
      this.me = null
      this.mode = 'unknown'
      setCurrentUser(null)
    },
  },
})
